import type { PositionData } from '@/features/side-drawer/logic/types'
import { Typography } from '@/ui/atoms/typography/Typography'
import { paths } from '@/config/paths'
import { PositionListItem } from '../PositionListItem'
import { EulerPositionListItem } from '../EulerPositionListItem'
import { PropsWithChildren } from 'react'
import * as Collapsible from '@/ui/atoms/collapsible/Collapsible'
import { ChevronDownIcon } from 'lucide-react'

import { HealthFactorBadge } from '@/ui/atoms/health-factor-badge/HealthFactorBadge'
import BigNumber from 'bignumber.js'
import { EmptyCard } from '../EmptyCard'
import { useSideDrawer } from '@/domain/hooks/useSideDrawer'

interface PositionsTabProps {
  positionData: PositionData
  setMobileMenuCollapsed: (collapsed: boolean) => void
}

export function PositionsTab({ positionData, setMobileMenuCollapsed }: PositionsTabProps) {
  const { positionsTab } = useSideDrawer()
  const { isPooledOpen, isIsolatedOpen, isEulerOpen, setIsPooledOpen, setIsIsolatedOpen, setIsEulerOpen } = positionsTab
  const hasPooledPositions = positionData.pooled.deposits.length > 0 || positionData.pooled.borrows.length > 0
  const hasIsolatedPositions = Object.keys(positionData.isolated.groupedByPairAddress).length > 0
  const hasEulerPositions =
    positionData.euler.elend.length > 0 || positionData.euler.eborrow.length > 0 || positionData.euler.earn.length > 0

  return (
    <div>
      <Collapsible.Root className="group/section mb-6" open={isPooledOpen} onOpenChange={setIsPooledOpen}>
        <Collapsible.Trigger className="w-full">
          <SectionHeading
            healthFactor={positionData.pooled.healthFactor}
            totalUSD={positionData.pooled.totalUSD}
            healthFactorText="Health"
          >
            Pooled lending
          </SectionHeading>
        </Collapsible.Trigger>
        <Collapsible.Content className="pl-6">
          {hasPooledPositions ? (
            <>
              {positionData.pooled.deposits.length > 0 && (
                <div>
                  {positionData.pooled.deposits.map((deposit) => {
                    return (
                      <PositionListItem
                        key={`pooled-deposit-${deposit.token.symbol}`}
                        token={deposit.token}
                        value={deposit.deposit}
                        type="deposit"
                      />
                    )
                  })}
                </div>
              )}

              {positionData.pooled.borrows.length > 0 && (
                <div>
                  <div>
                    {positionData.pooled.borrows.map((borrow) => {
                      return (
                        <PositionListItem
                          key={`pooled-borrow-${borrow.token.symbol}`}
                          token={borrow.token}
                          value={borrow.debt}
                          type="borrow"
                        />
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyCard
              header="No pooled positions"
              buttonText="Create pooled position"
              href={paths.easyBorrow}
              setMobileMenuCollapsed={setMobileMenuCollapsed}
            />
          )}
        </Collapsible.Content>
      </Collapsible.Root>

      <Collapsible.Root className="group/section mb-6" open={isIsolatedOpen} onOpenChange={setIsIsolatedOpen}>
        <Collapsible.Trigger className="w-full">
          <SectionHeading
            totalUSD={positionData.isolated.totalUSD}
            healthFactor={positionData.isolated.minHealthFactor}
            healthFactorText="Lowest health"
          >
            Isolated lending
          </SectionHeading>
        </Collapsible.Trigger>
        <Collapsible.Content>
          {hasIsolatedPositions ? (
            <>
              {Object.keys(positionData.isolated.groupedByPairAddress).length > 0 && (
                <div className="mt-2">
                  <div className="flex flex-col gap-4 pl-2">
                    {Object.entries(positionData.isolated.groupedByPairAddress).map(
                      ([pairAddress, { deposits, borrows, name, healthInfo }]) => {
                        return (
                          <div key={pairAddress} className="rounded-sm border border-border p-4">
                            <div className="flex items-center justify-between text-sm">
                              <Typography variant="prompt" className="text-sm">
                                {name}
                              </Typography>

                              {healthInfo && <HealthFactorBadge hf={healthInfo.healthFactor} size="small" />}
                            </div>

                            {deposits?.map((deposit) => {
                              return (
                                <PositionListItem
                                  key={`isolated-deposit-${deposit.pairAddress}-${deposit.collateralToken.symbol}`}
                                  token={deposit.collateralToken}
                                  value={deposit.lendAmount}
                                  type="deposit"
                                />
                              )
                            })}

                            {borrows?.map((borrow) => {
                              return (
                                <div key={`isolated-borrow-${borrow.pairAddress}-${borrow.borrowToken.symbol}`}>
                                  <PositionListItem
                                    token={borrow.borrowToken}
                                    value={borrow.borrowAmount}
                                    type="borrow"
                                  />
                                </div>
                              )
                            })}
                          </div>
                        )
                      },
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="pl-6">
              <EmptyCard
                header="No isolated positions"
                buttonText="Create isolated position"
                href={paths.earn}
                setMobileMenuCollapsed={setMobileMenuCollapsed}
              />
            </div>
          )}
        </Collapsible.Content>
      </Collapsible.Root>

      <Collapsible.Root className="group/section mb-6" open={isEulerOpen} onOpenChange={setIsEulerOpen}>
        <Collapsible.Trigger className="w-full">
          <SectionHeading totalUSD={positionData.euler.totalUSD}>Euler</SectionHeading>
        </Collapsible.Trigger>
        <Collapsible.Content className="pl-6">
          {hasEulerPositions ? (
            <>
              {/* eLend deposits */}
              {positionData.euler.elend.length > 0 && (
                <div className="mb-2">
                  <Typography variant="prompt" className="mb-1 text-muted-foreground text-xs">
                    Lend
                  </Typography>
                  {positionData.euler.elend.map((position) => (
                    <EulerPositionListItem
                      key={`elend-${position.vaultAddress}-${position.subAccountId}`}
                      token={position.token}
                      value={position.depositAmount}
                      type="deposit"
                      vaultName={position.vaultName}
                      marketName={position.market?.name}
                      subAccountId={position.subAccountId}
                    />
                  ))}
                </div>
              )}

              {/* eBorrow positions */}
              {positionData.euler.eborrow.length > 0 && (
                <div className="mb-2">
                  <Typography variant="prompt" className="mb-1 text-muted-foreground text-xs">
                    Borrow
                  </Typography>
                  {positionData.euler.eborrow.map((position) => (
                    <div
                      key={`eborrow-${position.liabilityVaultAddress}-${position.subAccountId}`}
                      className="mb-2 rounded-sm border border-border p-2"
                    >
                      <EulerPositionListItem
                        token={position.collateralToken}
                        value={position.collateralAmount}
                        type="deposit"
                        subAccountId={position.subAccountId}
                      />
                      <EulerPositionListItem
                        token={position.liabilityToken}
                        value={position.borrowAmount}
                        type="borrow"
                        subAccountId={position.subAccountId}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* eEarn deposits */}
              {positionData.euler.earn.length > 0 && (
                <div>
                  <Typography variant="prompt" className="mb-1 text-muted-foreground text-xs">
                    Earn
                  </Typography>
                  {positionData.euler.earn.map((position) => (
                    <EulerPositionListItem
                      key={`earn-${position.vaultAddress}`}
                      token={position.token}
                      value={position.depositAmount}
                      type="deposit"
                      vaultName={position.vaultName}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <EmptyCard
              header="No positions"
              buttonText="Explore Markets"
              href={paths.earn}
              setMobileMenuCollapsed={setMobileMenuCollapsed}
            />
          )}
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  )
}

interface SectionHeadingProps extends PropsWithChildren {
  totalUSD?: string
  healthFactor?: BigNumber
  healthFactorText?: string
}

function SectionHeading({ totalUSD, healthFactor, healthFactorText, children }: SectionHeadingProps) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <Typography variant="prompt" className="flex items-center justify-between gap-2 text-sm">
        {children} ({totalUSD})
      </Typography>

      <div className="flex items-center gap-2">
        {healthFactor && (
          <>
            <Typography variant="prompt" className="flex items-center justify-between gap-2 text-sm">
              {healthFactorText}
            </Typography>
            <HealthFactorBadge hf={healthFactor} size="small" />
          </>
        )}

        <ChevronDownIcon className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]/section:rotate-180" />
      </div>
    </div>
  )
}
